import React, { useEffect, useMemo } from 'react';
import styles from './resultatForm.module.css';
import genStyles from '../generale.module.css';
import SearchActionsMenu from './searchActionsMenu';

export default function ResultForm({ 
    data, 
    columns, 
    onSelectRow, 
    pageSize = 5, 
    tableHeight = '300px', 
    textpaginate = 'lignes',
    textnone = 'Aucune ligne',
    texteSearch = 'Rechercher', 
    sortConfig,
    setSortConfig,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    actionsRight = [],
    actionsButtonRight = [],
    selectedRows = [],
    bulkActions = [],
    emptyState = null,
    pinnedRows = []
}) {

    // Filtrage + tri
    const filteredData = useMemo(() => {
        if (!data) return [];
    
        const hasGroups = data.some(item => item.isFullRow);
    
        if (!hasGroups) {
            // Aucun groupe → tri classique
            let filtered = data;

            if (searchQuery && searchQuery.trim() !== '') {
                filtered = filtered.filter(item =>
                    columns.some(col => {   // <-- on prend toutes les colonnes
                        const val = item[col.key];
                        return val && val.toString().toLowerCase().includes(searchQuery.toLowerCase());
                    })
                );
            }
            
            if (sortConfig) {
                filtered = [...filtered].sort((a, b) => {
                    const aVal = a[sortConfig.key];
                    const bVal = b[sortConfig.key];
                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                });
            }
    
            return filtered;
        }
    
        // Avec groupes → séparer headers et utilisateurs
        const headers = data.filter(item => item.isFullRow);
        const users = data.filter(item => !item.isFullRow);
    
        // Filtrage
        const filteredUsers = searchQuery.trim() === ''
            ? users
            : users.filter(user =>
                columns
                    .filter(col => !col.render)
                    .some(col => {
                        const val = user[col.key];
                        return val && val.toString().toLowerCase().includes(searchQuery.toLowerCase());
                    })
            );
    
        // Reconstruire la liste en triant **chaque groupe séparément**
        const result = [];
        headers.forEach(header => {
            result.push(header);
            const groupUsers = filteredUsers.filter(u => u.groupId === header.id); // <-- filtrage correct
            const sortedGroupUsers = sortConfig
                ?[...groupUsers].sort((a, b) => {
                    const aVal = a[sortConfig.key];
                    const bVal = b[sortConfig.key];
                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                })
                : groupUsers;
            result.push(...sortedGroupUsers);
        });
    
        return result;
    }, [data, columns, searchQuery, sortConfig]);
     
    // Pagination
    const totalRows = filteredData.length;
    const totalPages = Math.ceil(totalRows / pageSize);
    const startRow = (currentPage - 1) * pageSize + 1;
    const endRow = Math.min(currentPage * pageSize, totalRows);

    const paginatedData = filteredData.slice(startRow - 1, endRow);


    // Correction pagination dynamique
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [data,totalPages]);


    const requestSort = (key) => {
        const col = columns.find((c) => c.key === key);
        if (col && col.sortable === false) return;

        let direction = 'asc';
        if (sortConfig?.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Affiche les pages autour de la page courante
    const getVisiblePages = () => {
        const range = 2;
        const pages = [];

        for (let i = Math.max(1, currentPage - range); i <= Math.min(totalPages, currentPage + range); i++) {
            pages.push(i);
        }

        return pages;
    };

    // Aucune donnée du tout (pas juste filtrée) : on affiche uniquement l'état vide personnalisé
    // Les lignes épinglées (ex : création en cours) restent en tête, hors tri/filtre/pagination
    const isTrulyEmpty = (!data || data.length === 0) && pinnedRows.length === 0;
    if (isTrulyEmpty && emptyState) {
        return <div>{emptyState}</div>;
    }

    return (
        <div>
            {/* Barre d’outils : recherche + actions, sur une seule ligne */}
            <div className={styles.toolbarRow}>
                <div className={styles.toolbarLeftGroup}>
                    {searchQuery !== undefined && setSearchQuery !== undefined && (
                        <div className={styles.searchInputCtn}>
                            <i
                                className={`fa fa-search ${styles.searchIcon}`}
                            ></i>
                            <input
                                type="text"
                                value={searchQuery}
                                placeholder={texteSearch}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className={`${genStyles.inputtCol} ${styles.searchInput}`}
                            />
                        </div>
                    )}
                </div>

                {/* Toutes les actions (sélection + reste), collées ensemble à droite */}
                <div className={styles.actionsRightGroup}>
                    {bulkActions.length > 0 && (
                        <SearchActionsMenu
                            actionsRight={bulkActions}
                            buttonIconClass={"fa-solid fa-list-check"}
                            titleAction={"Actions sur la sélection"}
                            disabled={selectedRows.length === 0}
                        />
                    )}
                    {actionsButtonRight}
                    {actionsRight.length > 0 && (
                        <SearchActionsMenu
                            actionsRight={actionsRight}
                            buttonIconClass={"fa-solid fa-ellipsis-vertical"}
                            titleAction={"Importer, Exporter, Partager"}
                        />
                    )}
                </div>
            </div>

            {totalRows > 0 || pinnedRows.length > 0 ? (
                <>
         
            {/* Tableau */}
            <div className={styles.tableCard}>
            <div className={styles.scrollablediv} style={{ maxHeight: tableHeight, overflowY: 'auto' }}>
                <table cellPadding="5" className={styles.customtable}>
                    <thead className={styles.stickyHeader} > 
                    <tr>
                        {columns.map((col,index) => (
                        <th
                            key={`${col.key}-${index}`}
                            colSpan={col.subColumns?.length || 1}
                            onClick={() => requestSort(col.key)}
                            style={{
                            cursor: col.sortable === false ? 'default' : 'pointer',
                            textAlign: col.align || 'left',
                            width: col.width || 'auto',
                            }}
                        >
                            {col.label}{' '}
                            {!col.subColumns && sortConfig?.key === col.key ? (
                                sortConfig.direction === 'asc' ? 
                                <i className="fa-solid fa-sort-up" style={{ marginLeft: 4 }}></i> : 
                                <i className="fa-solid fa-sort-down" style={{ marginLeft: 4 }}></i>
                            ) : null}
                        </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {[...pinnedRows, ...paginatedData].map((row, index) => (
                        <tr
                            key={`row-${row.id}-${index}`}
                            className={`${selectedRows.includes(row.id) ? styles.selectedRow : ""} ${index < pinnedRows.length ? styles.pinnedRow : ""}`}
                        >
                              {row.isFullRow ? (
                                    <td key={`cell-${row.id}`} colSpan={columns.length} style={{ fontWeight: 'bold' }}>
                                    {row.fullText}
                                    </td>
                                ) : (
                                columns.flatMap((col) => {
                                    if (col.subColumns) {
                                        if (col.render) {
                                        // Rendre directement ce que col.render renvoie (tableau de <td>)
                                        return col.render(row[col.key], row);
                                        } else {
                                        return col.subColumns.map((sub, i) => (
                                            <td key={`cell-${col.key}-${sub.key}-${row.id}-${i}`} 
                                                style={{
                                                    textAlign: sub.align || 'center',
                                                    width: sub.width || 'auto',
                                                }}>
                                            {/* contenu vide */}
                                            </td>
                                        ));
                                        }
                                    } else {
                                        return (
                                        <td
                                            key={`cell-${col.key}-${row.id}`}
                                            style={{
                                                textAlign: col.align || 'left',
                                                width: col.width || 'auto',
                                            }} 
                                            onClick={col.selectable ? () => onSelectRow?.(row) : undefined}
                                        >
                                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                                        </td>
                                        );
                                    }
                                })
                            )}

                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            </div>

            {/* Pagination + affichage total */}
                    <div className={styles.paginationContainer}>
                    {totalPages > 1 && (
                        <div className={styles.paginationButtons}>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={styles.paginationButton}
                                >
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>

                            {getVisiblePages().map((page) => (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`${styles.paginationButton} ${
                                page === currentPage ? styles.paginationButtonActive : ''
                                }`}
                            >
                                {page}
                            </button>
                            ))}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={styles.paginationButton}
                                >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                    </div>

                    {totalRows > 0 && (
                    <div className={styles.paginationtext}>
                        Affichage de {startRow} à {endRow} sur <span style={{ color: 'red' }}>{totalRows}</span> {textpaginate}
                    </div>
                    )}
                </>
            ) : (
                <div className={styles.paginationtext}>
                    {textnone}
                </div>
            )
            }
        </div>
    );
}
